/**
 * "The thing you were editing now lives under another id."
 *
 * A conflict handler that turns a rejected edit into a copy announces the
 * move here; an open editor swaps to the copy so keystrokes typed during
 * recovery land there. Delivered in this tab synchronously and to other tabs
 * over the offline BroadcastChannel.
 *
 * **A move is also remembered, so an editor that was not listening can ask.**
 * Delivery alone is lossy: a workspace tab that is hidden has had its effects
 * torn down, so it holds no subscription, and a move announced while it was
 * away used to reach nobody and then be gone. The editor came back holding an
 * id the server no longer has, and the next save would meet the same conflict
 * all over again. `moveFor` lets a surface ask about the entity it currently
 * holds, which is the only entity it can be wrong about.
 */

import { OFFLINE_CHANNEL, TAB_ID } from './wipe';

export interface EntityMove {
  /** The entity key the edit was addressed to. */
  from: string;
  /** The entity key that now holds it. */
  to: string;
  /** Anything the surface needs to switch without a round trip (the journal passes the copy's path). */
  detail?: Record<string, string>;
}

type Listener = (move: EntityMove) => void;
type MoveMessage = { type?: string; from?: string; move?: EntityMove };

const listeners = new Set<Listener>();

/**
 * The most recent move announced for each `from` key.
 *
 * Bounded and oldest-first, because nothing ever removes an entry: a move is
 * only useful to a surface still holding the old key, and once it swaps it asks
 * about the new one — which has no move — so the questions stop by themselves.
 * Moves come from conflict recovery and are rare; this is a safety net, not a
 * queue.
 */
const announced = new Map<string, EntityMove>();
const MAX_REMEMBERED = 50;

/** Remember a move so a surface that was not listening can still find it. */
function remember(move: EntityMove): void {
  announced.delete(move.from);
  announced.set(move.from, move);
  while (announced.size > MAX_REMEMBERED) {
    const oldest = announced.keys().next();
    if (oldest.done) break;
    announced.delete(oldest.value);
  }
}

/**
 * The move announced for `key` while nobody was listening, if there was one.
 *
 * Ask on mount, before subscribing: an effect that re-runs on every
 * `<Activity>` show would otherwise learn nothing about the time it spent
 * hidden. Asking about the key the surface currently holds makes it
 * self-limiting — a surface that has already swapped asks about the new key.
 */
export function moveFor(key: string): EntityMove | null {
  return announced.get(key) ?? null;
}

/** Forget every remembered move. For tests, and for a sign-out wipe. */
export function forgetMoves(): void {
  announced.clear();
}

/** Be told when an entity moves. Returns a disposer. */
export function subscribeToEntityMoved(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Hand the move to every subscriber in this tab, and remember it for the rest. */
function deliver(move: EntityMove): void {
  remember(move);
  listeners.forEach(listener => {
    try {
      listener(move);
    } catch {
      // One broken subscriber must not stop the others.
    }
  });
}

/** Announce a move to this tab and every other tab on the origin. */
export function notifyEntityMoved(move: EntityMove): void {
  deliver(move);
  if (typeof BroadcastChannel === 'undefined') return;
  const bc = new BroadcastChannel(OFFLINE_CHANNEL);
  bc.postMessage({ type: 'entity-moved', from: TAB_ID, move });
  bc.close();
}

/** Listen for other tabs' moves. Installed once at boot; returns a disposer. */
export function listenForEntityMoves(): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => undefined;
  const bc = new BroadcastChannel(OFFLINE_CHANNEL);
  bc.onmessage = (event: MessageEvent<MoveMessage>) => {
    const message = event.data;
    if (message?.type !== 'entity-moved' || message.from === TAB_ID || !message.move) return;
    deliver(message.move);
  };
  return () => bc.close();
}
